/**
 * Clinics API — MOCK implementation.
 *
 * Mirrors the real surface (Session 18.2):
 *   resolveClinic(code) → "enter" a clinic: validate the 6-digit code, RECORD
 *                         the visit in an in-memory store, and return the clinic
 *                         (shape: { data: { clinic: { clinicCode, clinicName } } }).
 *   listMyClinics()     → the clinics visited this session, newest entry first
 *                         (shape: { data: { clinics: [...] }, meta: { total } }).
 *
 * There's no DB in mock mode, so visits live in a module-level Map that resets
 * on reload — enough to exercise the flow and any future "my clinics" UI.
 *
 * Known mock clinic codes (match the backend seed + checkout mock):
 *   123456 → کلینیک زیبایی آرمان / Arman Beauty Clinic
 *   654321 → کلینیک پوست و مو رویا / Roya Skin & Hair Clinic
 * Any other code → 404 CLINIC_NOT_FOUND.
 *
 * Errors follow the contract envelope:
 *   err.response.data.error = { code, message, messageEn, details }
 */

const MOCK_DELAY_MS = 400;
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

function apiError(status, code, message = '', messageEn = '', details = {}) {
  const e = new Error(messageEn || message || code);
  e.response = { status, data: { error: { code, message, messageEn, details } } };
  return e;
}

/** Known clinics the mock recognizes, keyed by 6-digit code. Bilingual names. */
const MOCK_CLINICS = {
  '123456': { fa: 'کلینیک زیبایی آرمان', en: 'Arman Beauty Clinic' },
  '654321': { fa: 'کلینیک پوست و مو رویا', en: 'Roya Skin & Hair Clinic' },
};

/** In-memory visit store: clinicCode → { firstEnteredAt, lastEnteredAt, entryCount }. */
const VISITS = new Map();

// Pre-seed the demo user's visit history so mock mode mirrors the backend
// seed (the demo customer has already visited 123456 & 654321). This is also
// what the clinic-ticket dropdown reads (only clinics in your history), so the
// dropdown isn't empty on a fresh mock session. Entering a clinic via the
// ClinicEntryPage adds/refreshes entries on top of these.
(() => {
  const seed = [
    ['654321', 35],
    ['123456', 10],
  ];
  for (const [code, daysAgo] of seed) {
    const at = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString();
    VISITS.set(code, { firstEnteredAt: at, lastEnteredAt: at, entryCount: 1 });
  }
})();

/**
 * The clinics visited this session (incl. the pre-seeded demo history), newest
 * entry first. Shared with the clinic-tickets mock so the "message a clinic"
 * dropdown lists exactly the clinics in the user's history.
 *
 * @returns {Array<{clinicCode:string, clinicName:object, firstEnteredAt:string, lastEnteredAt:string, entryCount:number}>}
 */
export function getVisitedClinics() {
  return Array.from(VISITS.entries())
    .map(([clinicCode, v]) => ({
      clinicCode,
      clinicName: MOCK_CLINICS[clinicCode] || { fa: '', en: '' },
      firstEnteredAt: v.firstEnteredAt,
      lastEnteredAt: v.lastEnteredAt,
      entryCount: v.entryCount,
    }))
    .sort((a, b) => b.lastEnteredAt.localeCompare(a.lastEnteredAt));
}

/**
 * POST /user/clinic-visits — enter a clinic by its 6-digit code: record the
 * visit and resolve the clinic.
 *
 * @param {string} code 6-digit numeric clinic code
 * @returns {Promise<{ data: { clinic: { clinicCode: string, clinicName: {fa:string,en:string} } } }>}
 * @throws 404 CLINIC_NOT_FOUND when the code matches no clinic
 */
export async function resolveClinic(code) {
  await delay(MOCK_DELAY_MS);
  const normalized = String(code || '').trim();
  const clinicName = MOCK_CLINICS[normalized];
  if (!clinicName) {
    throw apiError(
      404,
      'CLINIC_NOT_FOUND',
      'کلینیکی با این کد یافت نشد.',
      'No clinic found with that code.',
    );
  }
  const now = new Date().toISOString();
  const existing = VISITS.get(normalized);
  VISITS.set(normalized, {
    firstEnteredAt: existing?.firstEnteredAt ?? now,
    lastEnteredAt: now,
    entryCount: (existing?.entryCount ?? 0) + 1,
  });
  // Mirrors the real backend: the clinic's face-capture policy travels with the
  // entry response and drives the dashboard's "capture my face" action.
  return { data: { clinic: { clinicCode: normalized, clinicName, capturePolicy: 'client_capture' } } };
}

/**
 * GET /user/clinic-visits — clinics visited this session, newest entry first.
 *
 * @returns {Promise<{ data: { clinics: Array<object> }, meta: { total: number } }>}
 */
export async function listMyClinics() {
  await delay(MOCK_DELAY_MS);
  const clinics = getVisitedClinics();
  return { data: { clinics }, meta: { total: clinics.length } };
}

export const clinicsApi = { resolveClinic, listMyClinics };
export default clinicsApi;
